import { useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import BlockRenderer from '../components/blocks/BlockRenderer'
import { colours, playerRootSx } from '../components/player/playerStyles'

// Development and test page: one sample of every block type, with inline
// data and no API calls, so automated accessibility checks can scan them
// all. Routed at /training/dev/blocks.

const svg = (markup) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 340" width="600" height="340">${markup}</svg>`,
  )}`

const SAMPLE_PICTURE = svg(
  '<rect width="600" height="340" fill="#E2E8F0"/>' +
    '<rect x="60" y="90" width="300" height="160" rx="16" fill="#FFFFFF" stroke="#242F40" stroke-width="4"/>' +
    '<rect x="360" y="140" width="150" height="110" rx="12" fill="#FFFFFF" stroke="#242F40" stroke-width="4"/>' +
    '<circle cx="150" cy="262" r="32" fill="#242F40"/><circle cx="440" cy="262" r="32" fill="#242F40"/>' +
    '<rect x="100" y="120" width="90" height="60" fill="#94A3B8"/>',
)

const content = {
  course: {
    title: 'Sample course',
    passMark: 80,
    retakePolicy: 'You can retake the assessment as many times as you need. Each attempt uses a new set of questions.',
    learningObjectives: [
      { text: 'Describe what each kind of screen in a course does.', outcomes: [] },
      { text: 'Use every activity with a keyboard alone.', outcomes: [] },
    ],
    framework: { name: 'Sample framework', statement: 'This course is mapped to the sample framework.' },
    sources: [
      { id: 'open-source', title: 'An openly licensed guide', publisher: 'A government department', year: 2024, licence: 'OGL', url: 'https://www.gov.uk/' },
      { id: 'cited-source', title: 'A guide that is named only', publisher: 'A professional body', year: 2023, licence: 'cite', url: 'https://example.org/' },
    ],
  },
  pathway: {
    id: 'sample',
    title: 'Sample pathway',
    estimatedMinutes: 35,
    practicalSignoff: {
      required: true,
      title: 'What your assessor will check',
      description: 'Your assessor will watch you do each of these in your workplace.',
      items: [
        { id: 'one', text: 'Carries out the first practical step safely.' },
        { id: 'two', text: 'Explains the second practical step.' },
      ],
    },
  },
  media: { 'sample.svg': SAMPLE_PICTURE },
  handout: '# Sample course\n\n## Key points\n\n- The **first** point to remember.\n- The second point.\n\n1. Do this first.\n2. Then do this.',
}

const module = {
  id: 'sample-module',
  title: 'Sample module',
  estimatedMinutes: 6,
  objectives: ['Explain the first idea.', 'Apply the second idea at work.'],
}

const BLOCKS = [
  { id: 'g-intro', type: 'courseIntro', body: 'This sample explains **why the course matters** to the person taking it.' },
  {
    id: 'g-how',
    type: 'howToUse',
    title: 'How to use this course',
    points: ['Use **Next** and **Back** at the bottom of each screen.', 'Your progress saves as you go.', 'Every activity works with a keyboard.'],
  },
  { id: 'g-module', type: 'moduleIntro' },
  {
    id: 'g-text',
    type: 'text',
    title: 'A text screen',
    body: 'A short paragraph of course text.\n\n- A first bullet point\n- A second bullet point\n\n### A sub-heading\n\nMore text with **bold** words.',
  },
  { id: 'g-picture', type: 'illustration', media: 'sample.svg', alt: 'A simple drawing of a vehicle seen from the side.', caption: 'A sample illustration.', body: 'Text that goes with the picture.' },
  { id: 'g-fact', type: 'callout', variant: 'keyFact', title: 'A key fact', body: 'A fact worth remembering.' },
  { id: 'g-duty', type: 'callout', variant: 'legalDuty', title: 'A legal duty', body: 'Something the law requires.' },
  { id: 'g-practice', type: 'callout', variant: 'inPractice', title: 'In practice', body: 'A tip for day-to-day work.' },
  { id: 'g-case', type: 'caseStudy', title: 'A case study', body: 'A crew arrives at an address and finds something unexpected.', question: 'What would you do first?' },
  {
    id: 'g-cards',
    type: 'revealCards',
    title: 'Reveal cards',
    intro: 'Open each card.',
    cards: [
      { id: 'a', front: 'First card', back: 'The back of the first card.' },
      { id: 'b', front: 'Second card', back: 'The back of the second card.' },
      { id: 'c', front: 'Third card', back: 'The back of the third card.' },
    ],
  },
  {
    id: 'g-hotspot',
    type: 'hotspot',
    title: 'Hotspots',
    intro: 'Find each area on the picture.',
    media: 'sample.svg',
    alt: 'A simple drawing of a vehicle seen from the side.',
    spots: [
      { id: 'window', label: 'The window', x: 24, y: 44, r: 8, body: 'About the window.' },
      { id: 'front-wheel', label: 'The front wheel', x: 73, y: 77, r: 7, body: 'About the front wheel.' },
      { id: 'rear-wheel', label: 'The rear wheel', x: 25, y: 77, r: 7, body: 'About the rear wheel.' },
    ],
  },
  {
    id: 'g-sequence',
    type: 'sequence',
    title: 'Put the steps in order',
    prompt: 'Put these steps in the right order.',
    items: [
      { id: 's1', text: 'The first step' },
      { id: 's2', text: 'The second step' },
      { id: 's3', text: 'The third step' },
      { id: 's4', text: 'The fourth step' },
    ],
    feedback: { correct: 'That is the right order.', incorrect: 'That is not the right order yet. Think about what has to happen first.' },
  },
  {
    id: 'g-match',
    type: 'match',
    title: 'Match the items',
    prompt: 'Choose the right category for each item.',
    categories: [
      { id: 'yes', label: 'Do this' },
      { id: 'no', label: 'Do not do this' },
    ],
    items: [
      { id: 'm1', text: 'A helpful action', category: 'yes', feedback: 'This is good practice.' },
      { id: 'm2', text: 'An unhelpful action', category: 'no', feedback: 'This makes things worse.' },
      { id: 'm3', text: 'Another helpful action', category: 'yes', feedback: 'This is also good practice.' },
    ],
  },
  {
    id: 'g-scenario',
    type: 'branchingScenario',
    title: 'A branching scenario',
    intro: 'Work through the scenario and choose what to do at each point.',
    start: 'n1',
    nodes: [
      {
        id: 'n1',
        text: 'A colleague makes a remark that worries you.',
        choices: [
          { id: 'c1', text: 'Speak to them privately.', quality: 'best', consequence: 'They listen and thank you.', next: 'n2' },
          { id: 'c2', text: 'Say nothing.', quality: 'poor', consequence: 'The remarks carry on.', next: 'n2' },
        ],
      },
      {
        id: 'n2',
        text: 'It happens again the next week.',
        choices: [
          { id: 'c3', text: 'Tell your manager.', quality: 'best', consequence: 'Your manager deals with it.', next: null },
          { id: 'c4', text: 'Mention it to a friend.', quality: 'acceptable', consequence: 'Your friend suggests telling your manager.', next: null },
        ],
      },
    ],
    debrief: 'Raising a concern early, and with the right person, is usually the best response.',
  },
  {
    id: 'g-check',
    type: 'knowledgeCheck',
    question: 'Which of these is the right answer?',
    multi: false,
    options: [
      { id: 'o1', text: 'The right answer', correct: true, feedback: 'Yes, this is right.' },
      { id: 'o2', text: 'A wrong answer', correct: false, feedback: 'No, this is not right.' },
      { id: 'o3', text: 'Another wrong answer', correct: false, feedback: 'No, this is not right either.' },
    ],
  },
  {
    id: 'g-check-multi',
    type: 'knowledgeCheck',
    title: 'Knowledge check with several answers',
    question: 'Which of these are right?',
    multi: true,
    options: [
      { id: 'o1', text: 'A right answer', correct: true, feedback: 'Yes.' },
      { id: 'o2', text: 'Another right answer', correct: true, feedback: 'Yes.' },
      { id: 'o3', text: 'A wrong answer', correct: false, feedback: 'No.' },
    ],
  },
  {
    id: 'g-reflection',
    type: 'reflection',
    title: 'Reflection',
    intro: 'Take a moment to think about your own work.',
    prompts: [
      { id: 'p1', text: 'What will you do differently after this course?' },
      { id: 'p2', text: 'Who could you talk to about this?' },
    ],
  },
  {
    id: 'g-video',
    type: 'video',
    title: 'A video',
    src: 'data:video/mp4;base64,AAAA',
    captions: 'data:text/vtt;charset=utf-8,WEBVTT%0A%0A00%3A00.000%20--%3E%2000%3A02.000%0ASample%20caption',
    transcript: 'This is the transcript of the sample video.',
  },
  { id: 'g-download', type: 'download', title: 'Your handout', body: 'Download the handout to keep the key points to hand.' },
  { id: 'g-summary', type: 'summary', title: 'Summary', points: ['The first key point.', 'The second key point.', 'The third key point.'] },
  {
    id: 'g-further',
    type: 'furtherLearning',
    title: 'Further learning',
    items: [{ source: 'open-source', note: 'A good place to start.' }, { source: 'cited-source' }],
  },
  { id: 'g-signoff', type: 'practicalSignoff', title: 'Practical sign-off', body: 'This pathway also needs a practical sign-off at work.' },
]

const Sample = ({ block }) => {
  const [state, setState] = useState(null)
  return (
    <Paper component="section" variant="outlined" sx={{ p: { xs: 2, sm: 4 }, mb: 3, borderColor: colours.hairline }} data-block-type={block.type}>
      <BlockRenderer
        block={block}
        module={module}
        content={content}
        completed={false}
        onStateChange={setState}
        onSave={async (saved) => {
          setState(saved)
          return true
        }}
      />
      <Box component="pre" sx={{ mt: 2, mb: 0, p: 1, fontSize: '0.875rem', color: colours.muted, bgcolor: '#F8FAFC', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }} data-testid={`state-${block.id}`}>
        {block.type}: {JSON.stringify(state)}
      </Box>
    </Paper>
  )
}

const BlockGallery = () => (
  <Box id="training-block-gallery" sx={{ ...playerRootSx, maxWidth: 860, mx: 'auto', px: { xs: 1.5, sm: 3 }, py: 3, boxSizing: 'border-box' }}>
    <Typography component="h1" sx={{ fontSize: '1.75rem', fontWeight: 700, mb: 3 }}>
      Block gallery
    </Typography>
    {BLOCKS.map((block) => (
      <Sample key={block.id} block={block} />
    ))}
  </Box>
)

export default BlockGallery
